import React, { useState, useRef, useEffect } from 'react';
import { FaThumbtack, FaCaretDown } from 'react-icons/fa';
import { useMemo } from "react";
import { MdDragIndicator } from 'react-icons/md';
import ExcelToolbar from './ExcelToolbar';
import ColumnSettings from './ColumnSettings';
import ColumnFilter from './ColumnFilter';
import Loader from '../../common/loader';
import { exportToExcel } from '../../../utils/excelExport';
import './style.css';

const ExcelTable = ({ 
  data = [], 
  columns = [], 
  onRowClick = () => {}, 
  onCellEdit = () => {},
  onExport = () => {},
  onBulkAction = () => {},
  onSelectionChange = () => {},
  onFilterChange = null,
  selectedData = [],
  editable = false,
  sortable = true,
  resizable = true,
  showToolbar = true,
  selectable = false,
  bulkActions = [],
  title = "Data Table",
  showToolbarExport = true,
  showToolbarFilter = true,
  showToolbarColumns = true,
  showToolbarFullscreen = true,
  toolbarLeftActions = null,
  toolbarRightActions = null,
  userId = null,
  customExportOptions = null,
  additionalExportOptions = null,
  filterValues = null,
  externalFilters = {},
  onFetchFilterValues = null,
  isLoading = false,
  loadingText = "Loading...",
  getRowClassName = null,
  getRowTitle = null,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pinnedColumns, setPinnedColumns] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const tableRef = useRef(null);
  const containerRef = useRef(null);
  const filterButtonRefs = useRef({});

  // Measure header text width (px) to ensure header labels are fully visible by default.
  const measureHeaderTextPx = useMemo(() => {
    // Fallback: approximate character width if canvas isn't available.
    const approx = (text = "") => String(text).length * 7.2;

    if (typeof document === "undefined") return approx;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext && canvas.getContext("2d");
    if (!ctx) return approx;

    // Keep this in-sync with `.header-text` font styling in `style.css`
    ctx.font =
      '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

    return (text = "") => ctx.measureText(String(text)).width;
  }, []);

  const getMinWidthForHeader = (label = "") => {
    const textPx = measureHeaderTextPx(label);

    // `.header-content` has padding: 6px 8px → 16px horizontal padding
    const paddingPx = 16;

    // `.header-content` gap + spacing between elements
    const gapsPx = 8;

    // Right side actions: pin button (18px) + sort icon (~14px) + small buffer
    const pinPx = 18;
    const sortPx = sortable ? 14 : 0;
    const actionsGapPx = sortable ? 4 : 0;
    const actionsPx = pinPx + actionsGapPx + sortPx;

    // Extra breathing room so we don't re-truncate due to rounding/subpixel rendering
    const bufferPx = 10;

    return Math.ceil(textPx + paddingPx + gapsPx + actionsPx + bufferPx);
  };

  // Storage key for this table's pinned columns (includes userId for per-user settings)
  const tableKey = title.toLowerCase().replace(/\s+/g, '-');
  const userPrefix = userId ? `user-${userId}-` : '';
  const storageKey = `${userPrefix}excel-table-pinned-${tableKey}`;
  const visibleStorageKey = `${userPrefix}excel-table-visible-${tableKey}`;

  // Load pinned columns from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setPinnedColumns(parsed);
        }
      }
    } catch (e) {
      console.error('Error loading pinned columns:', e);
    }
  }, [storageKey]);

  // Save pinned columns to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(pinnedColumns));
    } catch (e) {
      console.error('Error saving pinned columns:', e);
    }
  }, [pinnedColumns, storageKey]);

  const columnsSig = useMemo(() => {
    return (columns || [])
     .map((c) => `${c?.key ?? ""}:${c?.width ?? ""}`)
     .join("|");
  }, [columns]);

  // Initialize column widths and visible columns
  useEffect(() => {
    const initialWidths = {};
    const initialVisible = [];
    columns.forEach(col => {
      const base = col.width || 150;
      // Only auto-expand to fit header; never shrink existing widths
      initialWidths[col.key] = Math.max(base, getMinWidthForHeader(col.label));
      initialVisible.push(col.key);
    });

    setColumnWidths((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(initialWidths);
      if (prevKeys.length !== nextKeys.length) return initialWidths;
      for (const k of nextKeys) {
        if (prev?.[k] !== initialWidths[k]) return initialWidths;
      }
      return prev;
    });

    // Load visible columns from localStorage or use defaults
    try {
      const savedVisible = localStorage.getItem(visibleStorageKey);
      if (savedVisible) {
        const parsed = JSON.parse(savedVisible);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Filter to only include valid column keys
          const validKeys = parsed.filter(key => columns.some(col => col.key === key));
          if (validKeys.length > 0) {
            // Include any new columns that weren't in the saved list
            const allColumnKeys = columns.map(col => col.key);
            const newColumns = allColumnKeys.filter(key => !validKeys.includes(key));
            const finalVisible = [...validKeys, ...newColumns];
            setVisibleColumns(finalVisible);
            return;
          }
        }
      }
    } catch (e) {
      console.error('Error loading visible columns:', e);
    }

    setVisibleColumns((prev) => {
      if (prev?.length !== initialVisible.length) return initialVisible;
      for (let i = 0; i < initialVisible.length; i++) {
        if (prev[i] !== initialVisible[i]) return initialVisible;
      }
      return prev;
    });
 }, [columnsSig, visibleStorageKey]);

  // Save visible columns to localStorage when changed
  useEffect(() => {
    if (visibleColumns.length > 0) {
      try {
        localStorage.setItem(visibleStorageKey, JSON.stringify(visibleColumns));
      } catch (e) {
        console.error('Error saving visible columns:', e);
      }
    }
  }, [visibleColumns, visibleStorageKey]);

  // Filter visible columns and separate pinned/unpinned
  const displayColumns = columns.filter(col => visibleColumns.includes(col.key));
  
  const pinnedDisplayColumns = displayColumns.filter(col => pinnedColumns.includes(col.key));
  const unpinnedDisplayColumns = displayColumns.filter(col => !pinnedColumns.includes(col.key));

  // Calculate pinned columns total width for sticky positioning
  const getPinnedLeftOffset = (columnKey) => {
    let offset = selectable ? 40 : 0;
    for (const col of pinnedDisplayColumns) {
      if (col.key === columnKey) break;
      offset += columnWidths[col.key] || 150;
    }
    return offset;
  };

  // Sort data - only apply local filtering if onFilterChange is NOT provided
  // If onFilterChange is provided, filtering is done server-side
  const sortedData = React.useMemo(() => {
    let filteredData = data;
    
    // Only apply client-side filtering if no server-side filter callback
    if (!onFilterChange) {
      const activeFilters = Object.entries(columnFilters).filter(
        ([_, values]) => values && values.length > 0
      );
      
      if (activeFilters.length > 0) {
        filteredData = data.filter(row => {
          return activeFilters.every(([columnKey, allowedValues]) => {
            const column = columns.find(c => c.key === columnKey);
            let cellValue;
            
            // Use filterAccessor if provided (for complex objects like populated refs)
            if (column?.filterAccessor && typeof column.filterAccessor === 'function') {
              cellValue = column.filterAccessor(row[columnKey], row);
            } else if (columnKey.includes('.')) {
              // Handle nested objects (like sellerId.firstName)
              const keys = columnKey.split('.');
              cellValue = keys.reduce((obj, key) => obj?.[key], row);
            } else {
              cellValue = row[columnKey];
            }
            
            // Convert to string for comparison
            const stringValue = cellValue !== null && cellValue !== undefined 
              ? String(cellValue) 
              : '(Blank)';
            
            return allowedValues.includes(stringValue);
          });
        });
      }
    }
    
    // Then apply sorting
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columnFilters, columns, onFilterChange]);

  // Get unique values for a column (for filter dropdown)
  // Priority: 1. onFetchFilterValues (server), 2. filterValues prop, 3. current data
  const getUniqueColumnValues = (columnKey) => {
    // Check if external filter values are provided for this column
    if (filterValues && filterValues[columnKey] && Array.isArray(filterValues[columnKey])) {
      return filterValues[columnKey].sort((a, b) => {
        if (a === '(Blank)') return 1;
        if (b === '(Blank)') return -1;
        return String(a).localeCompare(String(b));
      });
    }
    
    // Extract from current data
    const column = columns.find(c => c.key === columnKey);
    const values = new Set();
    
    data.forEach(row => {
      let cellValue;
      
      // Use filterAccessor if provided (for complex objects like populated refs)
      if (column?.filterAccessor && typeof column.filterAccessor === 'function') {
        cellValue = column.filterAccessor(row[columnKey], row);
      } else if (columnKey.includes('.')) {
        // Handle nested objects (like sellerId.firstName)
        const keys = columnKey.split('.');
        cellValue = keys.reduce((obj, key) => obj?.[key], row);
      } else {
        cellValue = row[columnKey];
      }
      
      // Convert to string for display
      const stringValue = cellValue !== null && cellValue !== undefined 
        ? String(cellValue) 
        : '(Blank)';
      
      values.add(stringValue);
    });
    
    return Array.from(values).sort((a, b) => {
      if (a === '(Blank)') return 1;
      if (b === '(Blank)') return -1;
      return a.localeCompare(b);
    });
  };

  // Handle filter change for a column
  const handleFilterChange = (columnKey, selectedValues) => {
    const newFilters = {
      ...columnFilters,
      [columnKey]: selectedValues
    };
    setColumnFilters(newFilters);
    
    // If onFilterChange callback is provided, call it for server-side filtering
    if (onFilterChange) {
      onFilterChange(columnKey, selectedValues, newFilters);
    }
  };

  // Clear filter for a column
  const handleClearFilter = (columnKey) => {
    const newFilters = { ...columnFilters };
    delete newFilters[columnKey];
    setColumnFilters(newFilters);
    
    // If onFilterChange callback is provided, call it for server-side filtering
    if (onFilterChange) {
      onFilterChange(columnKey, [], newFilters);
    }
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setColumnFilters({});
    
    // If onFilterChange callback is provided, call it for server-side filtering
    if (onFilterChange) {
      onFilterChange(null, [], {});
    }
  };

  // Check if a column has active filter
  const hasActiveFilter = (columnKey) => {
    return columnFilters[columnKey] && columnFilters[columnKey].length > 0;
  };

  // Get count of active filters
  const activeFilterCount = Object.keys(columnFilters).filter(
    key => columnFilters[key] && columnFilters[key].length > 0
  ).length;

  // Pin/Unpin column
  const handleTogglePin = (e, columnKey) => {
    e.stopPropagation();
    setPinnedColumns(prev => 
      prev.includes(columnKey)
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  // Auto-fit column width to content
  const autoFitColumn = (columnKey) => {
    const column = columns.find(c => c.key === columnKey);
    if (!column) return;

    // Calculate width based on header text
    const headerWidth = getMinWidthForHeader(column.label);

    // Calculate max content width from data
    let maxContentWidth = headerWidth;
    sortedData.forEach(row => {
      const value = row[columnKey];
      let textLength = 0;
      if (value !== null && value !== undefined) {
        textLength = String(value).length;
      }
      const contentWidth = textLength * 8 + 24; // approx char width + padding
      if (contentWidth > maxContentWidth) {
        maxContentWidth = contentWidth;
      }
    });

    // Set new width (min 80, max 400)
    const newWidth = Math.min(400, Math.max(80, maxContentWidth));
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: newWidth
    }));
  };

  // Auto-fit all visible columns
  const autoFitAllColumns = () => {
    const newWidths = { ...columnWidths };
    displayColumns.forEach(column => {
      const headerWidth = getMinWidthForHeader(column.label);
      let maxContentWidth = headerWidth;
      sortedData.forEach(row => {
        const value = row[column.key];
        let textLength = 0;
        if (value !== null && value !== undefined) {
          textLength = String(value).length;
        }
        const contentWidth = textLength * 8 + 24;
        if (contentWidth > maxContentWidth) {
          maxContentWidth = contentWidth;
        }
      });
      newWidths[column.key] = Math.min(400, Math.max(80, maxContentWidth));
    });
    setColumnWidths(newWidths);
  };

  // Column resizing handlers
  const handleMouseDown = (e, columnKey) => {
    if (!resizable) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizingColumn(columnKey);
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];
    
    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff);
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Double-click to auto-fit column
  const handleDoubleClick = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    autoFitColumn(columnKey);
  };

  const handleCellClick = (rowData, columnKey, rowIndex) => {
    if (editable && columns.find(col => col.key === columnKey)?.editable) {
      onCellEdit(rowData, columnKey, rowIndex);
    }
  };

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns(prev => 
      prev.includes(columnKey) 
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleResetColumns = () => {
    setVisibleColumns(columns.map(col => col.key));
    setPinnedColumns([]);
    // Clear localStorage
    try {
      localStorage.removeItem(storageKey);
      localStorage.removeItem(visibleStorageKey);
    } catch (e) {
      console.error('Error clearing column settings:', e);
    }
  };

  const handleToggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleLocalExport = async () => {
    try {
      const result = await exportToExcel(sortedData, displayColumns, title.toLowerCase().replace(/\s+/g, '-'));
      if (result.success) {
        console.log('Export successful:', result.filename);
      } else {
        console.error('Export failed:', result.error);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleSelectAll = (checked) => {
    const newSelection = checked ? sortedData.map((_, index) => index) : [];
    setSelectedRows(newSelection);
    const selectedData = checked ? sortedData : [];
    onSelectionChange(selectedData);
  };

  const handleSelectRow = (rowIndex, checked) => {
    const newSelection = checked 
      ? [...selectedRows, rowIndex]
      : selectedRows.filter(index => index !== rowIndex);
    
    setSelectedRows(newSelection);
    const selectedData = newSelection.map(index => sortedData[index]);
    onSelectionChange(selectedData);
  };

  const isAllSelected = selectedRows.length === sortedData.length && sortedData.length > 0;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < sortedData.length;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const sameArr = (a = [], b = []) => a.length === b.length && a.every((v, i) => v === b[i]);

  useEffect(() => {
    if (Array.isArray(selectedData) && selectedData.length > 0) {
      const selectedIndices = [];
      selectedData.forEach((selectedItem) => {
        const id = selectedItem?._id;
        if (!id) return;
        const idx = sortedData.findIndex((item) => item?._id === id);
        if (idx !== -1) selectedIndices.push(idx);
      });
      setSelectedRows((prev) => (sameArr(prev, selectedIndices) ? prev : selectedIndices));
      return;
    }
    if (Array.isArray(selectedData) && selectedData.length === 0) {
      setSelectedRows((prev) => (prev.length === 0 ? prev : []));
    }
  }, [selectedData, sortedData]);

  // Render column header
  const renderColumnHeader = (column, isPinned, leftOffset = 0) => {
    const isPinnedCol = pinnedColumns.includes(column.key);
    const isFiltered = hasActiveFilter(column.key);
    const headerTitle = column.headerTitle || column.label;
    return (
      <th
        key={column.key}
        className={`excel-header ${sortable ? 'sortable' : ''} ${isPinned ? 'pinned-column' : ''} ${isFiltered ? 'filtered' : ''}`}
        style={{ 
          width: columnWidths[column.key],
          minWidth: columnWidths[column.key],
          maxWidth: columnWidths[column.key],
          ...(isPinned ? { left: leftOffset, position: 'sticky', zIndex: 12 } : {})
        }}
        title={headerTitle}
      >
        <div className="header-content">
          <span className="header-text" title={headerTitle}>{column.label}</span>
          <div className="header-actions">
            <button 
              className={`pin-btn ${isPinnedCol ? 'pinned' : ''}`}
              onClick={(e) => handleTogglePin(e, column.key)}
              title={isPinnedCol ? 'Unpin column' : 'Pin column'}
            >
              <FaThumbtack />
            </button>
            {/* Excel-style dropdown filter button */}
            <button 
              ref={el => filterButtonRefs.current[column.key] = el}
              className={`excel-dropdown-btn ${isFiltered ? 'filtered' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveFilterColumn(activeFilterColumn === column.key ? null : column.key);
              }}
              title="Click to filter"
            >
              <FaCaretDown />
            </button>
          </div>
        </div>
        {resizable && (
          <div
            className="resize-handle"
            onMouseDown={(e) => handleMouseDown(e, column.key)}
            onDoubleClick={(e) => handleDoubleClick(e, column.key)}
            title="Drag to resize, double-click to auto-fit"
          >
            <MdDragIndicator />
          </div>
        )}
      </th>
    );
  };

  // Render column cell
  const renderColumnCell = (column, row, rowIndex, isPinned, leftOffset = 0) => {
    return (
      <td
        key={column.key}
        className={`excel-cell ${column.editable ? 'editable' : ''} ${column.wrap ? 'wrap-cell' : ''} ${isPinned ? 'pinned-column' : ''}`}
        style={{ 
          width: columnWidths[column.key],
          minWidth: columnWidths[column.key],
          maxWidth: columnWidths[column.key],
          ...(isPinned ? { left: leftOffset, position: 'sticky', zIndex: 5 } : {})
        }}
        onClick={(e) => {
          if (!selectable) {
            e.stopPropagation();
            handleCellClick(row, column.key, rowIndex);
          } else {
            onRowClick(row, rowIndex);
          }
        }}
      >
        <div className={`cell-content ${column.wrap ? 'cell-wrap' : ''}`}>
          {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
        </div>
      </td>
    );
  };

  return (
    <div 
      className={`excel-table-container ${isFullscreen ? 'fullscreen' : ''}`}
      ref={containerRef}
    >
      {showToolbar && (
        <ExcelToolbar
          onExport={onExport}
          onLocalExport={handleLocalExport}
          onToggleColumns={() => setShowColumnSettings(true)}
          onToggleFullscreen={handleToggleFullscreen}
          onAutoFitAll={autoFitAllColumns}
          onBulkAction={onBulkAction}
          isFullscreen={isFullscreen}
          title={title}
          selectedCount={selectedRows.length}
          bulkActions={bulkActions}
          showExport={showToolbarExport}
          showFilter={showToolbarFilter}
          showColumns={showToolbarColumns}
          showFullscreen={showToolbarFullscreen}
          leftActions={toolbarLeftActions}
          rightActions={toolbarRightActions}
          customExportOptions={customExportOptions}
          additionalExportOptions={additionalExportOptions}
        />
      )}
      
      <div className="excel-table-wrapper excel-table-wrapper-relative" ref={tableRef}>
        {isLoading && (
          <div className="excel-table-loader-overlay">
            <Loader value={true} variant="overlay" size="medium" text={loadingText} />
          </div>
        )}
        <table className="excel-table">
          <thead>
            <tr>
              {selectable && (
                <th className="excel-header select-header pinned-column" style={{ left: 0, position: 'sticky', zIndex: 12 }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) input.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              {/* Render pinned columns first */}
              {pinnedDisplayColumns.map((column) => {
                const leftOffset = getPinnedLeftOffset(column.key);
                return renderColumnHeader(column, true, leftOffset);
              })}
              {/* Render unpinned columns */}
              {unpinnedDisplayColumns.map((column) => renderColumnHeader(column, false))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((row, rowIndex) => (
                <tr 
                  key={rowIndex} 
                  className={`excel-row ${selectedRows.includes(rowIndex) ? 'selected' : ''} ${getRowClassName ? getRowClassName(row, rowIndex) : ''}`}
                  title={getRowTitle ? getRowTitle(row, rowIndex) : undefined}
                  onClick={() => !selectable && onRowClick(row, rowIndex)}
                >
                  {selectable && (
                    <td className="excel-cell select-cell pinned-column" style={{ left: 0, position: 'sticky', zIndex: 5 }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(rowIndex)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectRow(rowIndex, e.target.checked);
                        }}
                      />
                    </td>
                  )}
                  {/* Render pinned columns first */}
                  {pinnedDisplayColumns.map((column) => {
                    const leftOffset = getPinnedLeftOffset(column.key);
                    return renderColumnCell(column, row, rowIndex, true, leftOffset);
                  })}
                  {/* Render unpinned columns */}
                  {unpinnedDisplayColumns.map((column) => renderColumnCell(column, row, rowIndex, false))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={displayColumns.length + (selectable ? 1 : 0)} className="no-data">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {isResizing && <div className="resize-overlay" />}
      
      {showColumnSettings && (
        <ColumnSettings
          columns={columns}
          visibleColumns={visibleColumns}
          pinnedColumns={pinnedColumns}
          onToggleColumn={handleToggleColumn}
          onTogglePin={handleTogglePin}
          onClose={() => setShowColumnSettings(false)}
          onResetColumns={handleResetColumns}
        />
      )}
      
      {activeFilterColumn && (
        <ColumnFilter
          columnKey={activeFilterColumn}
          columnLabel={columns.find(c => c.key === activeFilterColumn)?.label || activeFilterColumn}
          uniqueValues={getUniqueColumnValues(activeFilterColumn)}
          selectedValues={columnFilters[activeFilterColumn] || []}
          onFilterChange={(values) => handleFilterChange(activeFilterColumn, values)}
          onClear={() => handleClearFilter(activeFilterColumn)}
          onClose={() => setActiveFilterColumn(null)}
          onSort={(key, direction) => setSortConfig({ key, direction })}
          currentSortKey={sortConfig.key}
          currentSortDirection={sortConfig.direction}
          anchorEl={filterButtonRefs.current[activeFilterColumn]}
          onFetchValues={onFetchFilterValues}
        />
      )}
    </div>
  );
};

export default ExcelTable;
